import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { LoadingInstance } from 'element-plus/es/components/loading/src/loading';
import FileSaver from 'file-saver';
import { encryptBase64, encryptWithAes, generateAesKey, decryptWithAes, decryptBase64 } from '@/utils/crypto';
import { encrypt, decrypt } from '@/utils/jsencrypt';
import { tansParams, blobValidate, routerLocalePathProgram } from '@/utils/u7buy';
import { useUserSotre } from '@/store/modules/user';
import { errorCode } from '@/config/errorCode';
import { HttpStatus } from '@/config/responseEnum';
// import { getToken } from './token';
import { getLanguage, getLanguageSign } from './language';
import { useSetting } from '@/store/modules/setting';
import { useCart } from '@/store/modules/cart';
// import { removeToken } from '@/utils/token';
import { i18n } from '@/locales/index';

const encryptHeader = 'encrypt-key';
let downloadLoadingInstance: LoadingInstance;
// 是否显示重新登录
export const isRelogin = { show: false };
let currencyPromise: any = null; // 保存币种请求的 Promise
export const globalHeaders = () => {
  const { state } = useUserSotre();
  // accessToken
  return {
    Authorization: `Bearer ${state.accessToken || ''}`,
    clientid: import.meta.env.VITE_APP_CLIENT_ID
  };
};

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
axios.defaults.headers['clientid'] = import.meta.env.VITE_APP_CLIENT_ID;
// if (import.meta.env.VITE_APP_ENV === 'development') axios.defaults.headers['Version'] = 'wxf';
axios.defaults.withCredentials = true;
// 创建 axios 实例
const service = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  timeout: 50000
});

// 请求拦截器
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { state } = useUserSotre();
    const { state: stateSetting } = storeToRefs(useSetting());

    // accessToken
    // 对应国际化资源文件后缀
    config.headers['Content-Language'] = getLanguageSign() || getLanguage();
    // 国际化 币种
    const { state: stateCart } = useCart();
    // config.headers['Content-Currency'] = configuration.value.defaultCurrency;
    if (stateCart.touristId) config.headers['Tourist-Id'] = stateCart.touristId;
    if (stateCart.forterToken) config.headers['Forter-Token'] = stateCart.forterToken;
    const isToken = config.headers?.isToken === false;
    // 是否需要防止数据重复提交
    // const isRepeatSubmit = config.headers?.repeatSubmit === false;
    // 是否需要加密
    const isEncrypt = config.headers?.isEncrypt === 'true';
    // 判断是否需要币种，如果币种未设置，则发起请求并等待
    if (!stateSetting.value.configuration.defaultCurrency) {
      if (!currencyPromise) {
        // 发起币种获取请求，并存储 Promise
        currencyPromise = axios({
          baseURL: import.meta.env.VITE_APP_BASE_API,
          url: '/system/app/configuration',
          method: 'get'
        })
          .then(response => {
            stateSetting.value.configuration.defaultCurrency = response.data.data.defaultCurrency;
            return response.data.data.defaultCurrency;
          })
          .catch(error => {
            console.error('Failed to fetch currency:', error);
            throw error;
          })
          .finally(() => {
            currencyPromise = null; // 重置 Promise 状态
          });
      }
      // 等待币种请求完成
      await currencyPromise;
    }
    // 将币种添加到请求头
    config.headers['Content-Currency'] = stateSetting.value.configuration.defaultCurrency;
    if (state.accessToken && !isToken) {
      config.headers['Authorization'] = `Bearer ${state.accessToken || ''}`; // 让每个请求携带自定义token 请根据实际情况自行修改
      // 调试连接wxf接口
      config.headers['Version'] = 'member ';
    }
    // get请求映射params参数
    if (config.method === 'get' && config.params) {
      let url = config.url + '?' + tansParams(config.params);
      url = url.slice(0, -1);
      config.params = {};
      config.url = url;
    }
    // 禁止重复提交暂且不用
    // if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
    //   const requestObj = {
    //     url: config.url,
    //     data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
    //     time: new Date().getTime()
    //   };
    //   const sessionObj = cache.session.getJSON('sessionObj');
    //   if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
    //     cache.session.setJSON('sessionObj', requestObj);
    //   } else {
    //     const s_url = sessionObj.url; // 请求地址
    //     const s_data = sessionObj.data; // 请求数据
    //     const s_time = sessionObj.time; // 请求时间
    //     const interval = 500; // 间隔时间(ms)，小于此时间视为重复提交
    //     if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
    //       const message = '数据正在处理，请勿重复提交';
    //       console.warn(`[${s_url}]: ` + message);
    //       return Promise.reject(new Error(message));
    //     } else {
    //       cache.session.setJSON('sessionObj', requestObj);
    //     }
    //   }
    // }
    if (import.meta.env.VITE_APP_ENCRYPT === 'true') {
      // 当开启参数加密
      if (isEncrypt && (config.method === 'post' || config.method === 'put')) {
        // 生成一个 AES 密钥
        const aesKey = generateAesKey();
        config.headers[encryptHeader] = encrypt(encryptBase64(aesKey));
        config.data =
          typeof config.data === 'object'
            ? encryptWithAes(JSON.stringify(config.data), aesKey)
            : encryptWithAes(config.data, aesKey);
      }
    }
    // FormData数据去请求头Content-Type
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (res: AxiosResponse) => {
    const { addQueue, removeAllData, removeAccessToken, getRefreshToken } = useUserSotre();
    if (import.meta.env.VITE_APP_ENCRYPT === 'true') {
      // 加密后的 AES 秘钥
      const keyStr = res.headers[encryptHeader];
      // 加密
      if (keyStr != null && keyStr != '') {
        const data = res.data;
        // 请求体 AES 解密
        const base64Str = decrypt(keyStr);
        // base64 解码 得到请求头的 AES 秘钥
        const aesKey = decryptBase64(base64Str.toString());
        // aesKey 解码 data
        const decryptData = decryptWithAes(data, aesKey);
        // 将结果 (得到的是 JSON 字符串) 转为 JSON
        res.data = JSON.parse(decryptData);
      }
    }
    // 未设置状态码则默认成功状态
    const code = res.data.code || HttpStatus.SUCCESS;
    // 获取错误信息
    const msg = errorCode[code] || res.data.msg || errorCode['default'];
    // 二进制数据则直接返回
    if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
      return res.data;
    }

    if (code === HttpStatus.UNAUTHORIZED) {
      return new Promise(resolve => {
        // removeToken(); // token失效移除token
        removeAccessToken(); // token失效移除token
        // token失效 失败请求存入队列
        addQueue({ method: res.config.method as string, callback: () => resolve(service.request(res.config)) });
        getRefreshToken(); // 刷新token
        return;
      });
    } else if (code === HttpStatus.AUTH_EXPIRED) {
      removeAllData();
      routerLocalePathProgram('/login');
      return;
    } else if (code === HttpStatus.SERVER_ERROR) {
      ElMessage({ message: msg, type: 'error' });
      // return Promise.reject(new Error(msg));
    } else if (code === HttpStatus.WARN) {
      ElMessage({ message: msg, type: 'warning' });
      // return Promise.reject(new Error(msg));
    } else if (code === HttpStatus.NOT_FOUND) {
      routerLocalePathProgram('/error', 'replace');
      // router.replace({ path: '/error' });
    } else if (code === HttpStatus.FORBIDDEN) {
      routerLocalePathProgram('/', 'replace');
      // router.replace({ path: '/' });
    } else if (code === HttpStatus.SENSITIVENAME) {
      ElMessage({ message: i18n.global.t('member.UserInvalid'), type: 'warning' });
    } else if (code === HttpStatus.SENSITIVESTORENAME) {
      ElMessage({ message: i18n.global.t('member.StoreInvalid'), type: 'warning' });
    } else if (code !== HttpStatus.SUCCESS) {
      ElMessage({ message: msg, type: 'error' });
      // return Promise.reject("error");
    }
    return Promise.resolve(res.data);
  },
  (error: any) => {
    let { message } = error;
    if (message == 'Network Error') {
      message = 'System request timed out';
    } else if (message.includes('timeout')) {
      message = 'System request timed out';
    } else if (message.includes('Request failed with status code')) {
      message = 'System request timed out';
    }
    ElMessage({ message: message, type: 'error', duration: 5 * 1000 });
    return Promise.reject(error);
  }
);
// 通用下载方法
export function download(url: string, params: any, fileName: string) {
  downloadLoadingInstance = ElLoading.service({ text: 'Downloading data, please wait', background: 'rgba(0, 0, 0, 0.7)' });
  // prettier-ignore
  return service.post(url, params, {
        transformRequest: [
            (params: any) => {
                return tansParams(params);
            }
        ],
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        responseType: 'blob'
    }).then(async (resp: any) => {
        const isLogin = blobValidate(resp);
        if (isLogin) {
            const blob = new Blob([resp]);
            FileSaver.saveAs(blob, fileName);
        } else {
            const resText = await resp.data.text();
            const rspObj = JSON.parse(resText);
            const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default'];
            ElMessage.error(errMsg);
        }
        downloadLoadingInstance.close();
    }).catch((r: any) => {
        console.error(r);
        ElMessage.error('There is an error in downloading the file, please contact the administrator!');
        downloadLoadingInstance.close();
    });
}
// 导出 axios 实例
export default service;
